import React, { useEffect, useMemo, useRef } from "react";

import { Spinner } from "@performyard/py-components";

// Components
import EmptyChart from "../../../../../../../../components/EmptyChart";

import { ChartOptions } from "../../../../../../schemas/report";

// hooks
import useStore from "../../../../store";
// utils
import { ChartType as PYChart } from "../../../../store/enums";

import { useGetGridDataCount, useGetRawReviewReportData } from "../../../../../../api/hooks";

import { getFieldTypeFromSummnarizeByFieldValue, getLabelFromSummarizeByFieldValue } from "../../../../utils";
import { fillTimeRange } from "./utils";

import LineChart from "../ChartHooks/LineChart";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import { useDownloadImage } from "../ChartHooks/UseDownloadImage/useDownloadImage";
import { ChartContainer, VisualizationHeader } from "../Layout";
import css from "../Layout/Layout.module.scss";
import OptionSettings from "../shared/OptionSettings";

const { dataSeriesRangeOptions } = OptionSettings[PYChart.TrendlineChart];

const TrendlineChart = () => {
	const chartRef = useRef(null);
	const [getAppliedFilters, report, canViewSummary, getSortFromColumnObject] = useStore((state) => [
		state.getAppliedFilters,
		state.report,
		state.canViewSummary,
		state._formatGridSorts,
	]);

	const {
		summarizeBySetting,
		visualizeOption,
		aggregationOperations,
		anyAxisOptions,
		dataSeriesOptions,
		previousChart,
		previousVisualizeOption,
		getChartDataFromRawData,
		setVisualizeOptions,
		getAxisOptionsAsColumns,
	} = useChartOptions(PYChart.TrendlineChart);

	useEffect(() => {
		handleSetDefaultLineChartOptions();
	}, [summarizeBySetting?.[0]?.selection]);

	useEffect(() => {
		// If the selected column is not in the anyAxisOptions, set the default line chart options
		if (!anyAxisOptions?.some((option) => option.value === visualizeOption?.yAxis?.column)) {
			if (visualizeOption?.yAxis?.column) {
				handleSetDefaultLineChartOptions(true);
			}
		}
	}, [anyAxisOptions?.length]);

	const { downloadImage } = useDownloadImage(chartRef, {
		chartType: PYChart.TrendlineChart,
		xAxisLabel: "Metric",
	});

	const variables = {
		questionGroupingSessionID: report?.questionGroupingSessionID,
		columns: getAxisOptionsAsColumns(),
		filters: getAppliedFilters(),
		pageNumber: "1",
		pageSize: "100",
		sorts: visualizeOption?.dataSeries?.column
			? getSortFromColumnObject([
					{
						colId: visualizeOption?.dataSeries?.column,
						direction: "asc",
					},
				])?.[0]
			: null,
		summarizeBy: visualizeOption?.dataSeries?.rangeOption
			? [{ ...summarizeBySetting[0], selection: visualizeOption?.dataSeries?.rangeOption }]
			: summarizeBySetting,
		aggregationOperations,
	};

	const selectedRange = summarizeBySetting?.[0]?.["selection"];

	const handleSetDefaultLineChartOptions = (forceUpdate = false) => {
		const options: ChartOptions = {};
		// make sure the previous chart must be summarized by date typpe
		if (dataSeriesOptions?.length > 0) {
			const previousFieldTypeIsDate =
				getFieldTypeFromSummnarizeByFieldValue(previousVisualizeOption?.dataSeries?.column) === "date";
			options.dataSeries = {
				column:
					(previousFieldTypeIsDate ? previousVisualizeOption?.dataSeries?.column : undefined) ||
					visualizeOption?.dataSeries?.column ||
					dataSeriesOptions[dataSeriesOptions.length - 1].value,
				// For time series chart, initially get the default from the summarized by setting.
				rangeOption: selectedRange || dataSeriesRangeOptions[0].value,
			};
		}

		if (anyAxisOptions?.length > 0) {
			options.yAxis = {
				column:
					previousVisualizeOption?.yAxis?.column ||
					previousVisualizeOption?.xAxis?.column ||
					anyAxisOptions[0].value,
			};
			if (forceUpdate) {
				options.yAxis.column = anyAxisOptions[0].value;
			}
		}

		// Current behavior: when there is visualization option on first render, that means it came from saved report
		if ((visualizeOption && Object.keys(visualizeOption).length === 0) || forceUpdate) {
			let noChanges = true;
			const newVisualizeOption = { [PYChart.TrendlineChart]: { ...visualizeOption, ...options } };
			if (previousChart !== PYChart.TrendlineChart) {
				newVisualizeOption[previousChart] = null;
				noChanges = false;
			}
			setVisualizeOptions(newVisualizeOption, noChanges);
			return;
		}

		// Current behavior: when there is visualization option on first render, default to the first options
		if (visualizeOption && Object.keys(visualizeOption).length > 0) {
			const hasDataSeriesColumn = dataSeriesOptions.some(
				(option) => option.value === visualizeOption.dataSeries?.column
			);
			const hasYAxisColumn = anyAxisOptions.some((option) => option.value === visualizeOption.yAxis?.column);

			if (!hasDataSeriesColumn) {
				options.dataSeries = {
					column: dataSeriesOptions[dataSeriesOptions.length - 1].value,
					rangeOption: dataSeriesRangeOptions[0].value,
				};
			}

			if (!hasYAxisColumn) {
				options.yAxis = {
					column: anyAxisOptions[0].value,
				};
			}

			setVisualizeOptions({ [PYChart.TrendlineChart]: { ...visualizeOption, ...options } });
		}
	};

	const { data: rawData, isLoading: isRawDataLoading } = useGetRawReviewReportData(
		[variables],
		canViewSummary,
		report?.type
	)[0];

	const { data: countData } = useGetGridDataCount(variables, report?.type);
	const { labels, datasets = [] } = useMemo(
		() => fillTimeRange(getChartDataFromRawData(rawData), visualizeOption?.dataSeries?.rangeOption),
		[JSON.stringify(rawData), JSON.stringify(visualizeOption)]
	);

	const emtpyDatasets = datasets.filter((item) => item !== null).length === 0;

	const xLabelText = getLabelFromSummarizeByFieldValue(visualizeOption?.dataSeries?.column.replace(/^summary_/, ""));
	const yLabelText =
		anyAxisOptions &&
		anyAxisOptions.find((option) => option.value === visualizeOption?.yAxis?.column)?.labelWithAggregation;

	const selectedCountAggregation =
		anyAxisOptions &&
		anyAxisOptions
			.find((option) => option.value === visualizeOption?.yAxis?.column)
			?.labelWithAggregation.indexOf("(Count)") !== -1;

	return (
		<ChartContainer
			padding="16px 24px 16px 16px"
			height="calc(100% - 5px)"
			width="calc(100% - 20px)"
			testId="barChart"
		>
			{isRawDataLoading && (
				<div className={css.spinner}>
					<Spinner size={20} />
				</div>
			)}
			{!isRawDataLoading && emtpyDatasets && canViewSummary && <EmptyChart type={PYChart.BarChart} />}
			{!isRawDataLoading && !emtpyDatasets && (
				<>
					<VisualizationHeader
						viewingCount={countData?.reviewReportsCount?.count || 0}
						totalCount={countData?.reviewReportsCount?.count || 0}
						options={[
							{
								id: "download-image",
								label: "Download Image",
								action: downloadImage,
							},
						]}
					/>
					<LineChart
						key={"LineChart" + xLabelText + yLabelText}
						ref={chartRef}
						labels={labels}
						datasets={datasets}
						xLabelText={xLabelText}
						yLabelText={yLabelText}
						isCountAggregation={selectedCountAggregation}
					/>
				</>
			)}
		</ChartContainer>
	);
};

export default TrendlineChart;
